/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { buildThreadBlockerKey, getThreadBlockerMessageFromCode, normalizeThreadBlockerCode } from '@/lib/threadBlocker.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@/redux/selectors/pieveSelectors/constants.js';
import getNormalizedThreadId from '@/redux/selectors/pieveSelectors/lib/getNormalizedThreadId.js';
import getThreadBlockerActivityText from '@/redux/selectors/pieveSelectors/lib/getThreadBlockerActivityText.js';
import { threadContent, threadMeta } from '@/redux/selectors/pieveSelectors/threadCoreSelectors.js';

export const resolvedThreadBlockerCode = (state, threadId) => {
  const safeThreadId = getNormalizedThreadId(threadId);

  if (!safeThreadId) {
    return null;
  }

  const threadContentValue = threadContent(state, safeThreadId) || EMPTY_OBJECT;
  const threadRunStateValue = threadContentValue?.runState || EMPTY_OBJECT;
  const threadMetaValue = threadMeta(state, safeThreadId) || EMPTY_OBJECT;
  const blockerCandidates = [threadRunStateValue?.blockerCode, threadRunStateValue?.blockerReason, threadRunStateValue?.blockerMessage, threadMetaValue?.blockerCode, threadMetaValue?.blockerReason, threadMetaValue?.blockerMessage, threadMetaValue?.runState?.blockerCode, threadMetaValue?.runState?.blockerReason, threadMetaValue?.runState?.blockerMessage];

  for (const candidate of blockerCandidates) {
    const normalizedBlockerCode = normalizeThreadBlockerCode(candidate);

    if (normalizedBlockerCode) {
      return normalizedBlockerCode;
    }
  }

  const normalizedRunStateCode = normalizeThreadBlockerCode(threadRunStateValue?.state);

  if (normalizedRunStateCode) {
    return normalizedRunStateCode;
  }

  if (
    String(threadRunStateValue?.state || '')
      .trim()
      .toLowerCase() === 'error'
  ) {
    return 'retry_available';
  }

  const activity = Array.isArray(threadContentValue?.activity) ? threadContentValue.activity : EMPTY_ARRAY;

  for (let index = activity.length - 1; index >= 0; index -= 1) {
    const normalizedBlockerCode = normalizeThreadBlockerCode(getThreadBlockerActivityText(activity[index]));

    if (normalizedBlockerCode) {
      return normalizedBlockerCode;
    }
  }

  return null;
};

export const resolvedThreadBlockerMessage = (state, threadId) => {
  return getThreadBlockerMessageFromCode(resolvedThreadBlockerCode(state, threadId));
};

export const resolvedThreadBlockerKey = (state, threadId) => {
  const safeThreadId = getNormalizedThreadId(threadId);

  if (!safeThreadId) {
    return null;
  }

  const blockerCode = resolvedThreadBlockerCode(state, safeThreadId);
  const threadContentValue = threadContent(state, safeThreadId) || EMPTY_OBJECT;
  const threadMetaValue = threadMeta(state, safeThreadId) || EMPTY_OBJECT;
  const runId = threadContentValue?.runState?.runId || threadMetaValue?.runState?.runId || null;

  return buildThreadBlockerKey({
    blockerCode,
    runId
  });
};

let _prevBlockerCode = null;
let _prevBlockerKey = null;
let _prevBlockerResult = null;

export const resolvedThreadBlockerState = (state, threadId) => {
  const code = resolvedThreadBlockerCode(state, threadId);
  const key = resolvedThreadBlockerKey(state, threadId);

  if (code === _prevBlockerCode && key === _prevBlockerKey && _prevBlockerResult) {
    return _prevBlockerResult;
  }

  _prevBlockerCode = code;
  _prevBlockerKey = key;
  _prevBlockerResult = { code, message: getThreadBlockerMessageFromCode(code), key };
  return _prevBlockerResult;
};
